import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Calendar, 
  Clock, 
  CheckCircle, 
  XCircle, 
  AlertCircle, 
  Plus,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  FileText,
  Users,
  Briefcase,
  Sun,
  CalendarDays,
  Timer,
  Wallet,
  History,
  Send,
  X,
  User,
  Filter,
  TrendingUp,
  UserCheck,
  CalendarClock,
  CalendarX,
  Eye
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LeavesPage = () => {
  const { user, isAdmin, isManager } = useAuth();
  const token = localStorage.getItem('omnilink_token');
  
  const [loading, setLoading] = useState(false);
  const [balance, setBalance] = useState(null);
  const [myLeaves, setMyLeaves] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [allLeaves, setAllLeaves] = useState([]);
  const [leaveStats, setLeaveStats] = useState({
    todayOnLeave: [],
    upcomingLeaves: [],
    pendingCount: 0,
    approvedThisMonth: 0,
    rejectedThisMonth: 0
  });
  
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterMonth, setFilterMonth] = useState('all');
  
  // Pagination
  const [allLeavesPage, setAllLeavesPage] = useState(1);
  const [allLeavesTotal, setAllLeavesTotal] = useState(0);
  
  // Apply form state
  const [applyForm, setApplyForm] = useState({
    leave_type: 'paid',
    halfday_type: 'first_half',
    start_date: '',
    end_date: '',
    reason: ''
  });
  
  // Action modal state
  const [actionModal, setActionModal] = useState({ show: false, leave: null, action: '' });
  const [rejectionReason, setRejectionReason] = useState('');

  const fetchWithAuth = useCallback(async (endpoint) => {
    const response = await fetch(`${API_URL}${endpoint}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!response.ok) throw new Error('Request failed');
    return response.json();
  }, [token]);

  const postWithAuth = useCallback(async (endpoint, data) => {
    const response = await fetch(`${API_URL}${endpoint}`, {
      method: 'POST',
      headers: { 
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });
    return response.json();
  }, [token]);

  const putWithAuth = useCallback(async (endpoint, data) => {
    const response = await fetch(`${API_URL}${endpoint}`, {
      method: 'PUT',
      headers: { 
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });
    return response.json();
  }, [token]);

  const deleteWithAuth = useCallback(async (endpoint) => {
    const response = await fetch(`${API_URL}${endpoint}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return response.json();
  }, [token]);

  const fetchBalance = useCallback(async () => {
    try {
      const data = await fetchWithAuth('/api/leaves/balance');
      setBalance(data);
    } catch (error) {
      console.error('Error fetching balance:', error);
    }
  }, [fetchWithAuth]);

  const fetchMyLeaves = useCallback(async () => {
    try {
      const statusFilter = filterStatus !== 'all' ? `&status=${filterStatus}` : '';
      const data = await fetchWithAuth(`/api/leaves?page=1&limit=50${statusFilter}`);
      setMyLeaves(data.leaves || []);
    } catch (error) {
      console.error('Error fetching leaves:', error);
    }
  }, [fetchWithAuth, filterStatus]);

  const fetchPendingApprovals = useCallback(async () => {
    if (!isAdmin() && !isManager()) return;
    try {
      const data = await fetchWithAuth('/api/leaves/pending-approvals?page=1&limit=50');
      setPendingApprovals(data.leaves || []);
    } catch (error) {
      console.error('Error fetching pending approvals:', error);
    }
  }, [fetchWithAuth, isAdmin, isManager]);

  const fetchAllLeaves = useCallback(async () => {
    if (!isAdmin() && !isManager()) return;
    try {
      setLoading(true);
      let url = `/api/leaves/all?page=${allLeavesPage}&limit=20`;
      if (filterStatus !== 'all') url += `&status=${filterStatus}`;
      const data = await fetchWithAuth(url);
      setAllLeaves(data.leaves || []);
      setAllLeavesTotal(data.total || 0);
    } catch (error) {
      console.error('Error fetching all leaves:', error);
    } finally {
      setLoading(false);
    }
  }, [fetchWithAuth, isAdmin, isManager, allLeavesPage, filterStatus]);

  // Separate fetch for stats with larger limit to ensure accurate counts
  const [statsLeaves, setStatsLeaves] = useState([]);
  
  const fetchStatsLeaves = useCallback(async () => {
    if (!isAdmin() && !isManager()) return;
    try {
      // Fetch up to 500 leaves for accurate stats calculation
      const data = await fetchWithAuth('/api/leaves/all?page=1&limit=500');
      setStatsLeaves(data.leaves || []);
    } catch (error) {
      console.error('Error fetching stats leaves:', error);
    }
  }, [fetchWithAuth, isAdmin, isManager]);

  const calculateLeaveStats = useCallback(() => {
    const today = new Date().toISOString().split('T')[0];
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();
    
    // Use statsLeaves (larger dataset) for accurate stats
    const leavesForStats = statsLeaves.length > 0 ? statsLeaves : allLeaves;
    
    // Today on leave
    const todayOnLeave = leavesForStats.filter(l => 
      l.status === 'approved' && 
      l.start_date <= today && 
      l.end_date >= today
    );
    
    // Upcoming leaves (next 7 days)
    const next7Days = new Date();
    next7Days.setDate(next7Days.getDate() + 7);
    const upcomingLeaves = leavesForStats.filter(l => 
      l.status === 'approved' && 
      l.start_date > today && 
      l.start_date <= next7Days.toISOString().split('T')[0]
    );
    
    // Stats for current month
    const thisMonthLeaves = leavesForStats.filter(l => {
      const leaveDate = new Date(l.start_date);
      return leaveDate.getMonth() === currentMonth && leaveDate.getFullYear() === currentYear;
    });
    
    setLeaveStats({
      todayOnLeave,
      upcomingLeaves,
      pendingCount: pendingApprovals.length,
      approvedThisMonth: thisMonthLeaves.filter(l => l.status === 'approved').length,
      rejectedThisMonth: thisMonthLeaves.filter(l => l.status === 'rejected').length
    });
  }, [allLeaves, statsLeaves, pendingApprovals]);

  useEffect(() => {
    fetchBalance();
    fetchMyLeaves();
    if (isAdmin() || isManager()) {
      fetchPendingApprovals();
      fetchAllLeaves();
      fetchStatsLeaves();
    }
  }, [fetchBalance, fetchMyLeaves, fetchPendingApprovals, fetchAllLeaves, fetchStatsLeaves, isAdmin, isManager]);

  useEffect(() => {
    if (isAdmin() || isManager()) {
      calculateLeaveStats();
    }
  }, [allLeaves, statsLeaves, pendingApprovals, calculateLeaveStats, isAdmin, isManager]);

  const handleApplyLeave = async (e) => {
    e.preventDefault();
    
    if (!applyForm.start_date || !applyForm.end_date) {
      toast.error('Please select start and end dates');
      return;
    }
    
    if (!applyForm.reason || applyForm.reason.length < 5) {
      toast.error('Please provide a reason (minimum 5 characters)');
      return;
    }
    
    try {
      setLoading(true);
      const payload = {
        leave_type: applyForm.leave_type,
        start_date: applyForm.start_date,
        end_date: applyForm.leave_type === 'halfday' ? applyForm.start_date : applyForm.end_date,
        reason: applyForm.reason
      };
      
      if (applyForm.leave_type === 'halfday') {
        payload.halfday_type = applyForm.halfday_type;
      }
      
      await postWithAuth('/api/leaves', payload);
      toast.success('Leave application submitted successfully');
      setShowApplyModal(false);
      setApplyForm({
        leave_type: 'paid',
        halfday_type: 'first_half',
        start_date: '',
        end_date: '',
        reason: ''
      });
      fetchMyLeaves();
      fetchBalance();
    } catch (error) {
      toast.error('Failed to apply leave');
    } finally {
      setLoading(false);
    }
  };

  const handleLeaveAction = async () => {
    if (!actionModal.leave || !actionModal.action) return;
    
    if (actionModal.action === 'reject' && !rejectionReason) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    
    try {
      setLoading(true);
      await putWithAuth(`/api/leaves/${actionModal.leave.id}/action`, {
        action: actionModal.action,
        rejection_reason: actionModal.action === 'reject' ? rejectionReason : null
      });
      toast.success(`Leave ${actionModal.action === 'approve' ? 'approved' : 'rejected'} successfully`);
      setActionModal({ show: false, leave: null, action: '' });
      setRejectionReason('');
      fetchPendingApprovals();
      fetchAllLeaves();
    } catch (error) {
      toast.error('Action failed');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelLeave = async (leaveId) => {
    if (!window.confirm('Are you sure you want to cancel this leave application?')) return;
    
    try {
      await deleteWithAuth(`/api/leaves/${leaveId}`);
      toast.success('Leave application cancelled');
      fetchMyLeaves();
      fetchBalance();
    } catch (error) {
      toast.error('Failed to cancel leave');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-700 border-yellow-200',
      approved: 'bg-green-100 text-green-700 border-green-200',
      rejected: 'bg-red-100 text-red-700 border-red-200',
      cancelled: 'bg-gray-100 text-gray-700 border-gray-200'
    };
    const icons = {
      pending: <Clock className="h-3 w-3" />,
      approved: <CheckCircle className="h-3 w-3" />,
      rejected: <XCircle className="h-3 w-3" />,
      cancelled: <X className="h-3 w-3" />
    };
    return (
      <Badge variant="outline" className={`${styles[status]} flex items-center gap-1`}>
        {icons[status]}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const getLeaveTypeBadge = (type, halfdayType) => {
    const styles = {
      paid: 'bg-blue-100 text-blue-700',
      lwp: 'bg-orange-100 text-orange-700',
      halfday: 'bg-purple-100 text-purple-700'
    };
    const labels = {
      paid: 'Paid Leave',
      lwp: 'LWP',
      halfday: halfdayType === 'first_half' ? 'Half Day (AM)' : 'Half Day (PM)'
    };
    return <Badge className={styles[type]}>{labels[type]}</Badge>;
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-IN', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  // Admin/Manager View
  if (isAdmin() || isManager()) {
    return (
      <div className="space-y-6" data-testid="leave-management-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Leave Management</h1>
            <p className="text-gray-500 mt-1">Monitor and manage team leaves</p>
          </div>
          <Button onClick={() => { fetchPendingApprovals(); fetchAllLeaves(); fetchStatsLeaves(); }} variant="outline" className="gap-2">
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <Card className="border-l-4 border-l-red-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Today On Leave</p>
                  <p className="text-3xl font-bold text-red-600">{leaveStats.todayOnLeave.length}</p>
                </div>
                <div className="p-3 bg-red-100 rounded-full">
                  <CalendarX className="h-6 w-6 text-red-600" />
                </div>
              </div>
              {leaveStats.todayOnLeave.length > 0 && (
                <div className="mt-3 text-xs text-gray-500">
                  {leaveStats.todayOnLeave.slice(0, 2).map(l => l.user_name).join(', ')}
                  {leaveStats.todayOnLeave.length > 2 && ` +${leaveStats.todayOnLeave.length - 2} more`}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-yellow-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Pending Approval</p>
                  <p className="text-3xl font-bold text-yellow-600">{leaveStats.pendingCount}</p>
                </div>
                <div className="p-3 bg-yellow-100 rounded-full">
                  <Clock className="h-6 w-6 text-yellow-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Upcoming (7 days)</p>
                  <p className="text-3xl font-bold text-blue-600">{leaveStats.upcomingLeaves.length}</p>
                </div>
                <div className="p-3 bg-blue-100 rounded-full">
                  <CalendarClock className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-green-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Approved (Month)</p>
                  <p className="text-3xl font-bold text-green-600">{leaveStats.approvedThisMonth}</p>
                </div>
                <div className="p-3 bg-green-100 rounded-full">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-gray-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Rejected (Month)</p>
                  <p className="text-3xl font-bold text-gray-600">{leaveStats.rejectedThisMonth}</p>
                </div>
                <div className="p-3 bg-gray-100 rounded-full">
                  <XCircle className="h-6 w-6 text-gray-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Today On Leave Detail */}
        {leaveStats.todayOnLeave.length > 0 && (
          <Card className="bg-red-50 border-red-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2 text-red-700">
                <CalendarX className="h-5 w-5" />
                Employees On Leave Today
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-3">
                {leaveStats.todayOnLeave.map(leave => (
                  <div key={leave.id} className="bg-white rounded-lg px-4 py-2 border border-red-200 flex items-center gap-2">
                    <User className="h-4 w-4 text-red-600" />
                    <span className="font-medium">{leave.user_name}</span>
                    {getLeaveTypeBadge(leave.leave_type, leave.halfday_type)}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <Tabs defaultValue="pending" className="space-y-4">
          <TabsList className="grid w-full grid-cols-3 lg:w-auto lg:inline-grid">
            <TabsTrigger value="pending" className="gap-2">
              <AlertCircle className="h-4 w-4" />
              Pending ({pendingApprovals.length})
            </TabsTrigger>
            <TabsTrigger value="upcoming" className="gap-2">
              <CalendarClock className="h-4 w-4" />
              Upcoming ({leaveStats.upcomingLeaves.length})
            </TabsTrigger>
            <TabsTrigger value="all" className="gap-2">
              <History className="h-4 w-4" />
              All Leaves
            </TabsTrigger>
          </TabsList>

          {/* Pending Approvals Tab */}
          <TabsContent value="pending">
            <Card>
              <CardHeader>
                <CardTitle>Pending Approvals</CardTitle>
                <CardDescription>Review and action pending leave requests</CardDescription>
              </CardHeader>
              <CardContent>
                {pendingApprovals.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <CheckCircle className="h-12 w-12 mx-auto mb-4 text-green-300" />
                    <p className="font-medium">All caught up!</p>
                    <p className="text-sm">No pending leave requests</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {pendingApprovals.map(leave => (
                      <div key={leave.id} className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-4">
                            <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
                              <User className="h-5 w-5 text-indigo-600" />
                            </div>
                            <div>
                              <h4 className="font-semibold">{leave.user_name}</h4>
                              <p className="text-sm text-gray-500">{leave.user_role}</p>
                              <div className="flex items-center gap-4 mt-2 text-sm">
                                <span className="flex items-center gap-1">
                                  <Calendar className="h-4 w-4 text-gray-400" />
                                  {formatDate(leave.start_date)} - {formatDate(leave.end_date)}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Clock className="h-4 w-4 text-gray-400" />
                                  {leave.total_days} day(s)
                                </span>
                              </div>
                              <p className="text-sm text-gray-600 mt-2">
                                <strong>Reason:</strong> {leave.reason}
                              </p>
                              {leave.applied_via === 'email' && (
                                <Badge variant="outline" className="mt-2 text-xs">Applied via Email</Badge>
                              )}
                            </div>
                          </div>
                          <div className="flex flex-col items-end gap-2">
                            {getLeaveTypeBadge(leave.leave_type, leave.halfday_type)}
                            <div className="flex gap-2 mt-2">
                              <Button 
                                size="sm" 
                                className="bg-green-600 hover:bg-green-700"
                                onClick={() => setActionModal({ show: true, leave, action: 'approve' })}
                              >
                                <CheckCircle className="h-4 w-4 mr-1" />
                                Approve
                              </Button>
                              <Button 
                                size="sm" 
                                variant="destructive"
                                onClick={() => setActionModal({ show: true, leave, action: 'reject' })}
                              >
                                <XCircle className="h-4 w-4 mr-1" />
                                Reject
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Upcoming Leaves Tab */}
          <TabsContent value="upcoming">
            <Card>
              <CardHeader>
                <CardTitle>Upcoming Leaves (Next 7 Days)</CardTitle>
                <CardDescription>Approved leaves scheduled for the coming week</CardDescription>
              </CardHeader>
              <CardContent>
                {leaveStats.upcomingLeaves.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <CalendarDays className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                    <p className="font-medium">No upcoming leaves</p>
                    <p className="text-sm">No approved leaves in the next 7 days</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {leaveStats.upcomingLeaves.map(leave => (
                      <div key={leave.id} className="flex items-center justify-between border rounded-lg p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                            <User className="h-5 w-5 text-blue-600" />
                          </div>
                          <div>
                            <p className="font-medium">{leave.user_name}</p>
                            <p className="text-sm text-gray-500">
                              {formatDate(leave.start_date)} - {formatDate(leave.end_date)}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          {getLeaveTypeBadge(leave.leave_type, leave.halfday_type)}
                          <span className="text-sm text-gray-500">{leave.total_days} day(s)</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* All Leaves Tab */}
          <TabsContent value="all">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>All Leave Records</CardTitle>
                    <CardDescription>Complete leave history</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Select value={filterStatus} onValueChange={setFilterStatus}>
                      <SelectTrigger className="w-36">
                        <SelectValue placeholder="Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="approved">Approved</SelectItem>
                        <SelectItem value="rejected">Rejected</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center py-8">
                    <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
                  </div>
                ) : (
                  <>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b text-left">
                            <th className="pb-3 font-medium">Employee</th>
                            <th className="pb-3 font-medium">Type</th>
                            <th className="pb-3 font-medium">Dates</th>
                            <th className="pb-3 font-medium">Days</th>
                            <th className="pb-3 font-medium">Status</th>
                            <th className="pb-3 font-medium">Approved By</th>
                            <th className="pb-3 font-medium">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {allLeaves.map(leave => (
                            <tr key={leave.id} className="border-b hover:bg-gray-50">
                              <td className="py-3">
                                <div>
                                  <p className="font-medium">{leave.user_name}</p>
                                  <p className="text-xs text-gray-500">{leave.user_role}</p>
                                </div>
                              </td>
                              <td className="py-3">{getLeaveTypeBadge(leave.leave_type, leave.halfday_type)}</td>
                              <td className="py-3 text-sm">
                                {formatDate(leave.start_date)}
                                {leave.start_date !== leave.end_date && ` - ${formatDate(leave.end_date)}`}
                              </td>
                              <td className="py-3 text-sm">{leave.total_days}</td>
                              <td className="py-3">{getStatusBadge(leave.status)}</td>
                              <td className="py-3 text-sm text-gray-500">
                                {leave.approved_by_name || '-'}
                              </td>
                              <td className="py-3">
                                {leave.status === 'pending' && (
                                  <div className="flex gap-1">
                                    <Button 
                                      size="sm" 
                                      variant="ghost"
                                      className="h-8 w-8 p-0 text-green-600"
                                      onClick={() => setActionModal({ show: true, leave, action: 'approve' })}
                                    >
                                      <CheckCircle className="h-4 w-4" />
                                    </Button>
                                    <Button 
                                      size="sm" 
                                      variant="ghost"
                                      className="h-8 w-8 p-0 text-red-600"
                                      onClick={() => setActionModal({ show: true, leave, action: 'reject' })}
                                    >
                                      <XCircle className="h-4 w-4" />
                                    </Button>
                                  </div>
                                )}
                                {leave.status !== 'pending' && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-8 w-8 p-0"
                                    onClick={() => setSelectedLeave(leave)}
                                  >
                                    <Eye className="h-4 w-4" />
                                  </Button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    
                    {/* Pagination */}
                    {allLeavesTotal > 20 && (
                      <div className="flex items-center justify-between mt-4">
                        <p className="text-sm text-gray-500">
                          Showing {(allLeavesPage - 1) * 20 + 1} to {Math.min(allLeavesPage * 20, allLeavesTotal)} of {allLeavesTotal}
                        </p>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={allLeavesPage === 1}
                            onClick={() => setAllLeavesPage(p => p - 1)}
                          >
                            <ChevronLeft className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={allLeavesPage * 20 >= allLeavesTotal}
                            onClick={() => setAllLeavesPage(p => p + 1)}
                          >
                            <ChevronRight className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Action Modal */}
        {actionModal.show && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <Card className="w-full max-w-md mx-4">
              <CardHeader>
                <CardTitle>
                  {actionModal.action === 'approve' ? '✓ Approve Leave' : '✗ Reject Leave'}
                </CardTitle>
                <CardDescription>
                  {actionModal.leave?.user_name}&apos;s leave request
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
                  <p><strong>Type:</strong> {actionModal.leave?.leave_type}</p>
                  <p><strong>Dates:</strong> {formatDate(actionModal.leave?.start_date)} - {formatDate(actionModal.leave?.end_date)}</p>
                  <p><strong>Days:</strong> {actionModal.leave?.total_days}</p>
                  <p><strong>Reason:</strong> {actionModal.leave?.reason}</p>
                </div>
                
                {actionModal.action === 'reject' && (
                  <div>
                    <Label>Rejection Reason *</Label>
                    <Input
                      value={rejectionReason}
                      onChange={(e) => setRejectionReason(e.target.value)}
                      placeholder="Enter reason for rejection..."
                      className="mt-1"
                    />
                  </div>
                )}
                
                <div className="flex justify-end gap-2 pt-4">
                  <Button variant="outline" onClick={() => setActionModal({ show: false, leave: null, action: '' })}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleLeaveAction}
                    disabled={loading}
                    className={actionModal.action === 'approve' ? 'bg-green-600 hover:bg-green-700' : ''}
                    variant={actionModal.action === 'reject' ? 'destructive' : 'default'}
                  >
                    {loading ? 'Processing...' : actionModal.action === 'approve' ? 'Approve' : 'Reject'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Leave Detail Modal */}
        {selectedLeave && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <Card className="w-full max-w-md mx-4">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Leave Details</CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => setSelectedLeave(null)}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Employee</span>
                    <span className="font-medium">{selectedLeave.user_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Type</span>
                    {getLeaveTypeBadge(selectedLeave.leave_type, selectedLeave.halfday_type)}
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Dates</span>
                    <span>{formatDate(selectedLeave.start_date)} - {formatDate(selectedLeave.end_date)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Days</span>
                    <span>{selectedLeave.total_days}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Status</span>
                    {getStatusBadge(selectedLeave.status)}
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Reason</span>
                    <span className="text-right max-w-[200px]">{selectedLeave.reason}</span>
                  </div>
                  {selectedLeave.approved_by_name && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">{selectedLeave.status === 'approved' ? 'Approved By' : 'Rejected By'}</span>
                      <span>{selectedLeave.approved_by_name}</span>
                    </div>
                  )}
                  {selectedLeave.rejection_reason && (
                    <div className="pt-2 border-t">
                      <span className="text-gray-500 block mb-1">Rejection Reason</span>
                      <p className="text-red-600">{selectedLeave.rejection_reason}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    );
  }

  // Executive View - Apply for Leave
  return (
    <div className="space-y-6" data-testid="leave-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Leave Management</h1>
          <p className="text-gray-500 mt-1">Apply for leaves and track your applications</p>
        </div>
        <Button onClick={() => setShowApplyModal(true)} className="gap-2">
          <Plus className="h-4 w-4" />
          Apply for Leave
        </Button>
      </div>

      {/* Balance Cards */}
      {balance && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Paid Leave Balance</p>
                  <p className="text-3xl font-bold text-blue-600">
                    {balance.paid_leave?.available || 0}
                    <span className="text-lg text-gray-400">/{balance.paid_leave?.annual_quota || 12}</span>
                  </p>
                </div>
                <div className="p-3 bg-blue-100 rounded-full">
                  <Wallet className="h-6 w-6 text-blue-600" />
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Used: {balance.paid_leave?.total_used || 0} | Pending: {balance.paid_leave?.total_pending || 0}
              </p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-orange-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">LWP (Leave Without Pay)</p>
                  <p className="text-3xl font-bold text-orange-600">Unlimited</p>
                </div>
                <div className="p-3 bg-orange-100 rounded-full">
                  <Briefcase className="h-6 w-6 text-orange-600" />
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">No limit on unpaid leaves</p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-purple-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Half Day</p>
                  <p className="text-3xl font-bold text-purple-600">Unlimited</p>
                </div>
                <div className="p-3 bg-purple-100 rounded-full">
                  <Sun className="h-6 w-6 text-purple-600" />
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">First half or second half</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* My Leaves */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>My Leave Applications</CardTitle>
              <CardDescription>Your leave history and pending requests</CardDescription>
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-36">
                <SelectValue placeholder="Filter" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {myLeaves.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p className="font-medium">No leave applications</p>
              <p className="text-sm">Click &quot;Apply for Leave&quot; to submit a new request</p>
            </div>
          ) : (
            <div className="space-y-3">
              {myLeaves.map(leave => (
                <div key={leave.id} className="border rounded-lg p-4 hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      {getLeaveTypeBadge(leave.leave_type, leave.halfday_type)}
                      <div>
                        <p className="font-medium">
                          {formatDate(leave.start_date)}
                          {leave.start_date !== leave.end_date && ` - ${formatDate(leave.end_date)}`}
                        </p>
                        <p className="text-sm text-gray-500">{leave.total_days} day(s) • {leave.reason}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {getStatusBadge(leave.status)}
                      {leave.status === 'pending' && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => handleCancelLeave(leave.id)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                  {leave.rejection_reason && (
                    <p className="text-sm text-red-600 mt-2 bg-red-50 p-2 rounded">
                      <strong>Rejection Reason:</strong> {leave.rejection_reason}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Apply Leave Modal */}
      {showApplyModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Apply for Leave</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setShowApplyModal(false)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleApplyLeave} className="space-y-4">
                <div>
                  <Label>Leave Type</Label>
                  <Select 
                    value={applyForm.leave_type} 
                    onValueChange={(v) => setApplyForm({...applyForm, leave_type: v})}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="paid">Paid Leave</SelectItem>
                      <SelectItem value="lwp">Leave Without Pay (LWP)</SelectItem>
                      <SelectItem value="halfday">Half Day</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {applyForm.leave_type === 'halfday' && (
                  <div>
                    <Label>Half Day Type</Label>
                    <Select 
                      value={applyForm.halfday_type} 
                      onValueChange={(v) => setApplyForm({...applyForm, halfday_type: v})}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="first_half">First Half (Morning)</SelectItem>
                        <SelectItem value="second_half">Second Half (Afternoon)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Start Date</Label>
                    <Input
                      type="date"
                      value={applyForm.start_date}
                      onChange={(e) => setApplyForm({...applyForm, start_date: e.target.value, end_date: applyForm.leave_type === 'halfday' ? e.target.value : applyForm.end_date})}
                      className="mt-1"
                      min={new Date().toISOString().split('T')[0]}
                    />
                  </div>
                  {applyForm.leave_type !== 'halfday' && (
                    <div>
                      <Label>End Date</Label>
                      <Input
                        type="date"
                        value={applyForm.end_date}
                        onChange={(e) => setApplyForm({...applyForm, end_date: e.target.value})}
                        className="mt-1"
                        min={applyForm.start_date || new Date().toISOString().split('T')[0]}
                      />
                    </div>
                  )}
                </div>

                <div>
                  <Label>Reason</Label>
                  <Input
                    value={applyForm.reason}
                    onChange={(e) => setApplyForm({...applyForm, reason: e.target.value})}
                    placeholder="Enter reason for leave..."
                    className="mt-1"
                  />
                </div>

                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowApplyModal(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={loading}>
                    {loading ? 'Submitting...' : 'Submit Application'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default LeavesPage;
